import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const NotificationContext = createContext(null);

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { user, token } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);

    const connectWebSocket = useCallback(() => {
        if (!user?.id || wsRef.current?.readyState === WebSocket.OPEN) return;

        try {
            const ws = new WebSocket(`${WS_URL}/api/ws/${user.id}`);
            
            ws.onopen = () => {};

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    handleNotification(data);
                } catch (e) {
                    console.error('Failed to parse WebSocket message:', e);
                }
            };

            ws.onclose = () => {
                // Attempt to reconnect after 5 seconds
                reconnectTimeoutRef.current = setTimeout(() => {
                    connectWebSocket();
                }, 5000);
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
            };

            wsRef.current = ws;
        } catch (error) {
            console.error('Failed to create WebSocket:', error);
        }
    }, [user?.id]);

    const handleNotification = (data) => {
        const { type } = data;
        
        switch (type) {
            case 'notification':
                setNotifications(prev => [data.data, ...prev]);
                setUnreadCount(prev => prev + 1);
                toast.info(data.data.title, {
                    description: data.data.message,
                });
                break;
            case 'incident_created':
                toast.warning('Nouvel incident', {
                    description: `${data.data.title} - Sévérité: ${data.data.severity}`,
                });
                break;
            case 'incident_updated':
                toast.info('Incident mis à jour');
                break;
            case 'resource_created':
                toast.success('Nouvelle ressource ajoutée', {
                    description: data.data.name,
                });
                break;
            case 'resource_consumed':
                toast.info('Ressource consommée', {
                    description: `Restant: ${data.data.remaining}`,
                });
                break;
            case 'person_registered':
                toast.success('Nouvelle personne enregistrée', {
                    description: data.data.name,
                });
                break;
            case 'person_checkout':
                toast.info('Personne sortie du centre');
                break;
            case 'shift_created':
                toast.info('Nouveau quart créé', {
                    description: data.data.user_name,
                });
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        if (user?.id) {
            connectWebSocket();
        }

        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, [user?.id, connectWebSocket]);

    const markAsRead = (notifId) => {
        setNotifications(prev => 
            prev.map(n => n.id === notifId ? { ...n, read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
    };

    const clearNotifications = () => {
        setNotifications([]);
        setUnreadCount(0);
    };

    const value = {
        notifications,
        unreadCount,
        markAsRead,
        clearNotifications
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export default NotificationContext;
